<template>
    <div class="portrait-select-dropdown" :class="{ open: isOpen }">
        <button
            type="button"
            class="portrait-select-dropdown__trigger"
            :aria-expanded="isOpen ? 'true' : 'false'"
            aria-haspopup="listbox"
            :aria-label="ariaLabel"
            @click="toggle"
        >
            <i v-if="iconClass" class="portrait-select-dropdown__icon" :class="iconClass" aria-hidden="true"></i>
            <span class="portrait-select-dropdown__label">{{ selectedLabel }}</span>
            <i
                class="portrait-select-dropdown__caret"
                :class="isOpen ? 'i-andy-chevron-up' : 'i-andy-chevron-down'"
                aria-hidden="true"
            ></i>
        </button>

        <template v-if="isOpen">
            <div class="portrait-select-dropdown__backdrop" @click="close"></div>
            <ul class="portrait-select-dropdown__menu" role="listbox">
                <li
                    v-for="option in options"
                    :key="String(option.id)"
                    class="portrait-select-dropdown__option"
                    :class="{ selected: isOptionSelected(option) }"
                    role="option"
                    :aria-selected="isOptionSelected(option) ? 'true' : 'false'"
                    @click="onSelect(option)"
                >
                    <span class="portrait-select-dropdown__option-text">{{ option.label }}</span>
                    <span v-if="option.quantity > 0" class="portrait-select-dropdown__option-quantity">{{ option.quantity }}</span>
                </li>
            </ul>
        </template>
    </div>
</template>

<script>
/**
 * Selector desplegable portrait (patrón BUILD-7482).
 * Presentacional: no conoce categorías/estados; solo opciones + selección.
 */
export default {
    name: 'PortraitSelectDropdown',
    props: {
        options: {
            type: Array,
            default() {
                return []
            }
        },
        value: {
            type: [String, Number, Boolean],
            default: false
        },
        iconClass: {
            type: String,
            default: 'i-andy-quantity-stack'
        },
        ariaLabel: {
            type: String,
            default: ''
        },
        /**
         * Control externo del desplegable (acordeón: un solo abierto a la vez).
         * Si no se pasa, el componente gestiona su propio estado.
         * Nota: no tipamos como Boolean estricto (Vue 2 coerce null/undefined).
         */
        open: {
            default: undefined
        }
    },
    data() {
        return {
            internalOpen: false
        }
    },
    computed: {
        isOpen() {
            return typeof this.open === 'boolean' ? this.open : this.internalOpen
        },
        selectedOption() {
            const selected = this.options.find((option) => this.isOptionSelected(option))
            return selected || this.options[0] || null
        },
        selectedLabel() {
            return this.selectedOption ? this.selectedOption.label : ''
        }
    },
    methods: {
        isOptionSelected(option) {
            if (!option) {
                return false
            }
            if (Object.prototype.hasOwnProperty.call(option, 'selected')) {
                return !!option.selected
            }
            return option.id === this.value
        },
        toggle() {
            this.setOpen(!this.isOpen)
        },
        close() {
            this.setOpen(false)
        },
        setOpen(nextOpen) {
            if (typeof this.open !== 'boolean') {
                this.internalOpen = nextOpen
            }
            this.$emit(nextOpen ? 'open' : 'close')
            this.$emit('update:open', nextOpen)
        },
        onSelect(option) {
            this.$emit('select', option)
            this.close()
        }
    }
}
</script>

<style lang="scss" scoped>
.portrait-select-dropdown {
    position: relative;
    width: 100%;
    max-width: 100%;
    height: 45px;
    z-index: $z-menu-top;

    &.open {
        z-index: $z-menu-top + 1;
    }

    &__trigger {
        @include display-flex();
        @include align-items(center);
        @include border-radius(4px);
        width: 100%;
        height: 100%;
        padding: 0 12px;
        gap: 10px;
        background-color: #fff;
        border: 1px solid $color-neutral-300;
        cursor: pointer;
        text-align: left;
    }

    &__icon {
        flex: 0 0 auto;
        font-size: 22px;
        line-height: 1;
        color: var(--neutro-s-20-app);
    }

    &__label {
        @include font-size(sm);
        @include text-ellipsis();
        flex: 1 1 auto;
        font-family: $text-sbold;
        color: $color-neutral-800;
    }

    &__caret {
        flex: 0 0 auto;
        font-size: 12px;
        line-height: 1;
        color: $color-neutral-800;
    }

    &__backdrop {
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        z-index: $z-menu-top - 1;
    }

    &__menu {
        @include border-radius(4px);
        position: absolute;
        top: calc(100% + 4px);
        left: 0;
        width: 100%;
        max-height: 55vh;
        margin: 0;
        padding: 4px 0;
        list-style: none;
        background-color: #fff;
        border: 1px solid $color-neutral-300;
        box-shadow: 0 6px 16px rgba(0, 0, 0, 0.12);
        overflow-y: auto;
        z-index: $z-menu-top;
    }

    &__option {
        @include display-flex();
        @include align-items(center);
        @include justify-content(space-between);
        gap: 10px;
        min-height: 44px;
        padding: 0 14px;
        cursor: pointer;

        &:hover:not(.selected) {
            background-color: $color-neutral-200;
        }

        &.selected {
            background-color: var(--accent-app);

            .portrait-select-dropdown__option-text {
                font-family: $text-sbold;
                color: #fff;
            }
        }
    }

    &__option-text {
        @include font-size(sm);
        @include text-ellipsis();
        font-family: $text;
        color: $color-neutral-800;
    }

    &__option-quantity {
        @include border-radius(4px);
        padding: 3px 6px;
        font-family: $text-bold;
        font-size: 14px;
        color: #fff;
        background-color: $color-error-500;
    }
}
</style>
